import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

export const GenerateCbamReportPayloadSchema = z.object({
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  year: z.string().optional(),
});

export type GenerateCbamReportPayload = z.infer<typeof GenerateCbamReportPayloadSchema>;

export const GenerateCustomsRegisterPayloadSchema = z.object({
  batchType: z.string(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  year: z.string().optional(),
  groupByShippingMarks: z.boolean().optional(),
});

export type GenerateCustomsRegisterPayload = z.infer<typeof GenerateCustomsRegisterPayloadSchema>;

export const batchesReportsContract = c.router({
  sendRiskAnalysisReport: {
    method: 'POST',
    path: '/batches/send-risk-analysis-report',
    body: z.object({ batchId: z.string() }),
    responses: {
      200: z.object({ message: z.string() }),
    },
  },
  generateRiskAnalysisReport: {
    method: 'POST',
    path: '/batches/generate-risk-analysis-report',
    body: z.object({ batchId: z.string() }),
    responses: {
      200: z.object({ message: z.string() }),
    },
  },
  generateInvoice: {
    method: 'POST',
    path: '/batches/generate-invoice',
    body: z.object({ batchId: z.string() }),
    responses: {
      200: z.object({ message: z.string() }),
    },
  },
  generateProformaB2bInvoice: {
    method: 'POST',
    path: '/batches/generate-proforma-b2b-invoice',
    body: z.object({ batchId: z.string(), templateId: z.string() }),
    responses: {
      200: z.object({ message: z.string(), reportsGenerated: z.number() }),
    },
  },
  generateCustomsRegister: {
    method: 'POST',
    path: '/batches/generate-customs-register',
    body: GenerateCustomsRegisterPayloadSchema,
    responses: {
      200: z.object({ message: z.string() }),
    },
  },
  generateCbamReport: {
    method: 'POST',
    path: '/batches/generate-cbam-report',
    body: GenerateCbamReportPayloadSchema,
    responses: {
      200: z.object({ message: z.string() }),
    },
  },
  generateWriteOffReport: {
    method: 'POST',
    path: '/batches/:id/generate-write-off-report',
    pathParams: z.object({ id: z.string() }),
    body: c.noBody(),
    responses: {
      200: z.object({ message: z.string() }),
    },
  },
  batchReport: {
    method: 'POST',
    path: '/batches/batch-report',
    body: z.object({ batchId: z.string(), setRequestedBy: z.boolean().optional() }),
    responses: {
      200: z.object({ message: z.string() }),
    },
  },
  downloadBatchReport: {
    method: 'POST',
    path: '/batches/download-batch-report',
    body: z.object({ batchId: z.string(), reportId: z.string().optional() }),
    responses: {
      200: z.any(),
      204: z.object({ message: z.string() }),
    },
  },
  downloadBulkBatchesReport: {
    method: 'POST',
    path: '/batches/download-bulk-batches-report',
    body: z.object({ batchIds: z.array(z.string()) }),
    responses: {
      200: z.any(),
    },
  },
  exportScreenshots: {
    method: 'POST',
    path: '/batches/:id/export-screenshots',
    pathParams: z.object({ id: z.string() }),
    body: c.noBody(),
    responses: {
      200: z.any(),
    },
  },
  retrieveUtbReportData: {
    method: 'POST',
    path: '/batches/:id/retrieve-utb-report-data',
    pathParams: z.object({ id: z.string() }),
    body: z.object({ page: z.number().optional() }),
    responses: {
      200: z.object({ data: z.any() }),
    },
  },
  generateUtbReport: {
    method: 'POST',
    path: '/batches/:id/generate-utb-report',
    pathParams: z.object({ id: z.string() }),
    body: c.noBody(),
    responses: {
      200: z.object({ message: z.string() }),
    },
  },
  downloadUtbReport: {
    method: 'POST',
    path: '/batches/:id/download-utb-report',
    pathParams: z.object({ id: z.string() }),
    body: z.object({ reportId: z.string().optional() }),
    responses: {
      200: z.any(),
      204: z.object({ message: z.string() }),
    },
  },
  downloadBulkUtbReport: {
    method: 'POST',
    path: '/batches/download-bulk-utb-report',
    body: z.object({ batchIds: z.array(z.string()) }),
    responses: {
      200: z.any(),
    },
  },
  runRiskAnalysisCalculations: {
    method: 'GET',
    path: '/batches/:id/run-risk-analysis-calculations',
    pathParams: z.object({ id: z.string() }),
    responses: {
      200: z.object({ message: z.string() }),
    },
  },
  updateWriteOffList: {
    method: 'POST',
    path: '/batches/update-write-off-list',
    contentType: 'multipart/form-data',
    body: z.object({ batchId: z.string(), file: z.any().optional() }),
    responses: {
      200: z.object({ message: z.string() }),
    },
  },
});
